import React, { useState, useContext, useEffect } from "react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import "./shopHoursModal.css";
import { CombinedContext } from "../../../context/combinedContext";

const DEFAULT_HOURS = [
	{ dayEN: "Monday", dayES: "Lunes", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Tuesday", dayES: "Martes", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Wednesday", dayES: "Miércoles", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Thursday", dayES: "Jueves", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Friday", dayES: "Viernes", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Saturday", dayES: "Sábado", open: "9:00", close: "17:00", closed: false },
	{ dayEN: "Sunday", dayES: "Domingo", open: "09:00", close: "17:00", closed: false },
];
const ShopHoursModal = ({ open, onClose, shopData, setShopData }) => {
	const { language, setUserData, userData} = useContext(CombinedContext);

	const [localHours, setLocalHours] = useState([]);
	const [saving, setSaving] = useState(false);

	// ✅ Load shop hours into local state

	useEffect(() => {
		if (shopData?.openingHours?.length > 0) {
			setLocalHours([...shopData.openingHours]);
		} else {
			setLocalHours([...DEFAULT_HOURS]);
		}
	}, [shopData]);

	if (!open) return null;

	const handleHourChange = (index, field, value) => {
		setLocalHours((prev) => {
			const updated = [...prev];
			const day = { ...updated[index] };
	
			const toMinutes = (time) => {
				if (!time) return null;
				const [h, m] = time.split(":").map(Number);
				return h * 60 + m;
			};
	
			const newOpen = field === "open" ? value : day.open;
			const newClose = field === "close" ? value : day.close;
	
			if (!day.closed && newOpen && newClose) {
				const openMins = toMinutes(newOpen);
				const closeMins = toMinutes(newClose);
	
				// ❌ reject invalid change
				if (openMins >= closeMins) {
					return prev;
				}
			}
	
			// ✅ accept change
			day[field] = value;
			updated[index] = day;
			return updated;
		});
	};
	

	const toggleClosed = (index) => {
		const updated = [...localHours];
		updated[index].closed = !updated[index].closed;

		// 👇 Auto-clear hours if closed
		if (updated[index].closed) {
			updated[index].open = "00:00";
			updated[index].close = "00:00";
		}
		setLocalHours(updated);
	};

	const handleSave = async () => {
		try {
			setSaving(true)
			const res = await companyBasedRequest.updateShopHours(
				shopData.shopId,
				{ openingHours: localHours },
				userData.cognitoTokens.AccessToken
			);
	
			setShopData((prev) => {
				const hasSteps =
					prev?.setup &&
					prev?.setup.steps &&
					typeof prev.setup.steps === "object";
	
				return {
					...prev,
					openingHours: localHours,
	
					// ✅ only update steps if they exist
					...(hasSteps && {
						setup: {
							...prev.setup,
							steps: {
								...prev.setup.steps,
								hours: true,
							},
						},
					}),
				};
			});
	
			// ✅ backend is source of truth
			setUserData((prev) => ({
				...prev,
				shop: res.shop,
			}));
	
			onClose();
		} catch (err) {
			console.error("Failed to update hours:", err);
		} finally {
			setSaving(false)
		}
	};

	function CustomTimePicker({ value, onChange, minTime, maxTime }) {
		const HOURS = [1,2,3,4,5,6,7,8,9,10,11,12];
		const MINUTES = ["00","15","30","45"];
		const PERIOD = ["AM","PM"];
	
		const normalize = (t) => {
			if (!t) return null;
	
			if (t.includes("AM") || t.includes("PM")) {
				const [hm, p] = t.split(" ");
				const [h, m] = hm.split(":");
				return { h: Number(h), m, p };
			}
	
			const [h24, m] = t.split(":").map(Number);
			const p = h24 >= 12 ? "PM" : "AM";
			let h12 = h24 % 12;
			if (h12 === 0) h12 = 12;
	
			return { h: h12, m: String(m).padStart(2, "0"), p };
		};
	
		const parsed = normalize(value) || { h: 9, m: "00", p: "AM" };
		const { h, m, p } = parsed;
	
		const toMinutes = (hh, mm, pp) => {
			let hour24 = hh % 12;
			if (pp === "PM") hour24 += 12;
			return hour24 * 60 + Number(mm);
		};
	
		const minObj = normalize(minTime);
		const maxObj = normalize(maxTime);
	
		const minMins = minObj ? toMinutes(minObj.h, minObj.m, minObj.p) : null;
		const maxMins = maxObj ? toMinutes(maxObj.h, maxObj.m, maxObj.p) : null;
	
		const isAllowed = (hh, mm, pp) => {
			const t = toMinutes(hh, mm, pp);
			if (minMins !== null && t <= minMins) return false;
			if (maxMins !== null && t >= maxMins) return false;
			return true;
		};
	
		const update = (nh = h, nm = m, np = p) => {
			if (!isAllowed(nh, nm, np)) return;
		
			// convert to 24h before sending out
			let hour24 = nh % 12;
			if (np === "PM") hour24 += 12;
		
			const formatted = `${String(hour24).padStart(2, "0")}:${nm}`;
		
			onChange(formatted);
		};
		return (
			<div className="sf-timepicker">
				<select value={h} onChange={(e)=>update(Number(e.target.value),m,p)}>
					{HOURS.filter(hh => MINUTES.some(mm => isAllowed(hh,mm,p)))
						.map(hh => <option key={hh} value={hh}>{hh}</option>)}
				</select>
	
				<select value={m} onChange={(e)=>update(h,e.target.value,p)}>
					{MINUTES.filter(mm => isAllowed(h,mm,p))
						.map(mm => <option key={mm} value={mm}>{mm}</option>)}
				</select>
	
				<select value={p} onChange={(e)=>update(h,m,e.target.value)}>
					{PERIOD.filter(pp => isAllowed(h,m,pp))
						.map(pp => <option key={pp} value={pp}>{pp}</option>)}
				</select>
			</div>
		);
	}
	

	return (
		<div className="sf-book-overlay" onClick={onClose}>
			<div className="sf-book-modal" onClick={(e) => e.stopPropagation()}>
				<div className="sf-profile-modal-header">
					<h2 className="sf-profile-modal-title">{translations[language].profile.sidebar.hours.title}</h2>
					<button className="sf-book-close" onClick={onClose}>
						✕
					</button>
				</div>

				<div className="sf-book-body">
					{localHours.map((day, index) => (
						<div className="sf-hours-row" key={index}>
							<div className="sf-hours-day">{language === "es" ? day.dayES : day.dayEN}</div>

							{!day.closed ? (
								<div className="sf-hours-inputs">
									<CustomTimePicker
										value={day.open}
										maxTime={day.close}
										onChange={(v) => handleHourChange(index, "open", v)}
									/>

									<span> - </span>

									<CustomTimePicker
										value={day.close}
										minTime={day.open}
										onChange={(v) => handleHourChange(index, "close", v)}
									/>
								</div>
							) : (
								<div className="sf-hours-closed">
									{translations[language].profile.sidebar.hours.closed}
								</div>
							)}


							<button className="sf-hours-toggle" onClick={() => toggleClosed(index)}>
								{day.closed ? (language === "es" ? "Marcar abierto" : "Mark Open") : language === "es" ? "Marcar cerrado" : "Mark Closed"}
							</button>
						</div>
					))}
				</div>

				<div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ShopHoursModal;
